import { $isListNode, ListNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import {
  $findMatchingParent,
  $getNearestNodeOfType,
  mergeRegister,
} from '@lexical/utils';
import { Typography } from '@mui/material';
import {
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  $setSelection,
  FOCUS_COMMAND,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { ReactNode, useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button/Button';
import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { Bold01Icon } from '@/components/icons/Bold01Icon';
import { Strikethrough01Icon } from '@/components/icons/Strikethrough01Icon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useTextEditorContext } from '@/components/TextEditor/contexts/textEditor.context';
import {
  $isFontSizeNode,
  ALLOWED_FONT_SIZES,
  type AllowedFontSize,
  DEFAULT_FONT_SIZE,
} from '@/components/TextEditor/plugins/FontSizePlugin/FontSizeNode';
import { SET_FONT_SIZE_COMMAND } from '@/components/TextEditor/plugins/FontSizePlugin/FontSizePlugin';
import { TableActionToolbar } from '@/components/TextEditor/plugins/ToolbarPlugin/TableActionToolbar';
import { TableInsertButton } from '@/components/TextEditor/plugins/ToolbarPlugin/TableInsertButton';
import { isSelectionInTable } from '@/components/TextEditor/plugins/ToolbarPlugin/tableUtils';
import { Block } from '@/components/TextEditor/plugins/ToolbarPlugin/ToolbarPlugin.utils';
import { TextEditorKind } from '@/components/TextEditor/TextEditor';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';

import { SlideEditorBlockDropdown } from './SlideEditor.blockDropdown';
import { SlideEditorHighlightButton } from './SlideEditor.highlightButton';
import { useSlideEditorPromptContext } from './SlideEditor.promptContext';
import {
  SlideEditorToolbar,
  useSlideEditorToolbarContext,
} from './SlideEditor.toolbar';
import { SlideEditorToolbarDivider } from './SlideEditor.toolbarDivider';
import { useToolbarDropdown } from './SlideEditor.toolbarDropdown';

const LowPriority = 1;

/**
 * Effective rendered font size (px) for a heading block type, matching the
 * FONT_SIZING ramp in useTextEditorClasses. Returns undefined for non-heading
 * blocks (callers fall back to the editor's default size).
 *
 * h1/h2 are theme-independent. h3 uses FONT_SIZING.lg, which is 1.0625rem
 * (17px) under the presentationContent theme (slide editor) and 1rem (16px)
 * otherwise (e.g. diagram text notes under userGeneratedContent) — so it must
 * be resolved per theme rather than hardcoded.
 */
export function headingFontSize(
  blockType: Block,
  isPresentation: boolean
): number | undefined {
  switch (blockType) {
    case 'h1':
      return 20; // FONT_SIZING.xxl = 1.25rem
    case 'h2':
      return 18; // FONT_SIZING.xl = 1.125rem
    case 'h3':
      return isPresentation ? 17 : 16; // FONT_SIZING.lg = 1.0625rem : 1rem
    default:
      return undefined;
  }
}

/**
 * Returns the font size label to display in the toolbar dropdown.
 * Headings show their CSS-defined size; body text shows the explicit
 * FontSizeNode size or the default.
 */
export function getFontSizeLabel(
  currentFontSize: number | 'mixed' | null,
  blockType: Block,
  isPresentation: boolean,
  defaultSize: number = DEFAULT_FONT_SIZE
): string {
  if (currentFontSize === 'mixed') return EMPTY_CONTENT_HYPHEN;

  // Explicit non-default size takes priority (e.g., user overrode heading size)
  if (currentFontSize != null && currentFontSize !== defaultSize) {
    return `${currentFontSize}pt`;
  }

  // Headings show their CSS-defined size; body shows the default
  const headingSize = headingFontSize(blockType, isPresentation);
  return `${headingSize ?? defaultSize}pt`;
}

/**
 * Returns whether a given dropdown size option should be bolded (active).
 * The active size is the heading's mapped size when in a heading, the
 * explicit FontSizeNode size when set, or DEFAULT_FONT_SIZE for body text.
 */
export function isActiveSize(
  size: number,
  currentFontSize: number | 'mixed' | null,
  blockType: Block,
  isPresentation: boolean,
  defaultSize: number = DEFAULT_FONT_SIZE
): boolean {
  if (currentFontSize === 'mixed') return false;

  // Explicit non-default size
  if (currentFontSize != null && currentFontSize !== defaultSize) {
    return size === currentFontSize;
  }

  // Heading or default body
  const headingSize = headingFontSize(blockType, isPresentation);
  return size === (headingSize ?? defaultSize);
}

interface SlideEditorBodyToolbarProps {
  // Required by the TextEditor ToolbarPlugin prop type
  textEditorKind: TextEditorKind;
  /** Additional buttons rendered before the default block/format/table buttons */
  additionalButtons?: ReactNode;
  /** Additional buttons rendered after the default block/format/table buttons */
  endButtons?: ReactNode;
}

export function SlideEditorBodyToolbar({
  textEditorKind: _textEditorKind,
  additionalButtons,
  endButtons,
}: SlideEditorBodyToolbarProps) {
  return (
    <SlideEditorToolbar>
      {additionalButtons}
      <SlideEditorBodyToolbarButtons />
      {endButtons}
    </SlideEditorToolbar>
  );
}

/**
 * The default body toolbar buttons: block dropdown, bold, strikethrough, table controls.
 * Must be rendered inside a SlideEditorToolbar context and a LexicalComposer context.
 * Exported so consumers can compose their own toolbar layout or replace with custom buttons.
 */
export function SlideEditorBodyToolbarButtons() {
  const { activeDropdown, setActiveDropdown } = useSlideEditorToolbarContext();
  const promptContext = useSlideEditorPromptContext();
  const [editor] = useLexicalComposerContext();
  const [blockType, setBlockType] = useState<Block>('paragraph');
  const [isBold, setIsBold] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isInTable, setIsInTable] = useState(false);
  // null = no font size applied, 'mixed' = multiple sizes in selection
  const [currentFontSize, setCurrentFontSize] = useState<
    AllowedFontSize | 'mixed' | null
  >(null);

  const blockDropdown = useToolbarDropdown('block');
  const fontSizeDropdown = useToolbarDropdown('fontSize');

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? (element.getTag() as Block)
            : (element.getType() as Block);
          setBlockType(type);
        }
      }
      setIsBold(selection.hasFormat('bold'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));

      // Detect font size across the selection. Collapsed selections check the
      // anchor's parent; range selections check all selected nodes.
      const selectedNodes = selection.getNodes();
      const sizes = new Set<number | null>();
      for (const node of selectedNodes) {
        // Only check text nodes and FontSizeNodes for size detection.
        // Block-level nodes and linebreaks don't participate in font sizing
        // and would inject null into the set, causing a false 'mixed' state.
        if (!$isTextNode(node) && !$isFontSizeNode(node)) continue;
        // $findMatchingParent starts from the parent, so check the node itself first
        const fsNode = $isFontSizeNode(node)
          ? node
          : $findMatchingParent(node, $isFontSizeNode);
        // Normalize unsized text to DEFAULT_FONT_SIZE so that a selection
        // spanning unsized text and a FontSizeNode(DEFAULT) reads as uniform.
        sizes.add(fsNode ? fsNode.getFontSize() : DEFAULT_FONT_SIZE);
      }
      if (sizes.size > 1) {
        setCurrentFontSize('mixed');
      } else {
        const onlySize = sizes.values().next().value;
        setCurrentFontSize((onlySize as AllowedFontSize) ?? null);
      }
    }
    setIsInTable(isSelectionInTable());
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        FOCUS_COMMAND,
        () => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setSelection(selection.clone());
          }
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  return (
    <>
      {/* Block type dropdown */}
      <div ref={blockDropdown.containerRef}>
        <SlideEditorBlockDropdown
          editor={editor}
          blockType={blockType}
          isOpen={activeDropdown === 'block'}
          onClick={() =>
            setActiveDropdown((prev) => (prev === 'block' ? null : 'block'))
          }
          onClose={() =>
            setActiveDropdown((prev) => (prev === 'block' ? null : prev))
          }
        />
      </div>

      {/* Font size dropdown — hidden in table cells and when editing a template prompt */}
      {!isInTable &&
        !(promptContext?.isTemplate && promptContext.customPrompt) && (
          <SlideEditorFontSizeDropdown
            containerRef={fontSizeDropdown.containerRef}
            currentFontSize={currentFontSize}
            blockType={blockType}
            isOpen={activeDropdown === 'fontSize'}
            onToggle={() =>
              setActiveDropdown((prev) =>
                prev === 'fontSize' ? null : 'fontSize'
              )
            }
            onClose={() =>
              setActiveDropdown((prev) => (prev === 'fontSize' ? null : prev))
            }
          />
        )}

      {/* Format buttons: Bold, Strikethrough */}
      {/* Underline is intentionally omitted — the body editor serializes to
          markdown, and there is no markdown transformer for underline, so the
          formatting would be lost on save/reload. */}
      <Button
        size="xs"
        variant="transparent"
        square
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
        sx={isBold ? ACTIVE_FORMAT_BUTTON_SX : undefined}
      >
        <Bold01Icon size={16} />
      </Button>
      <Button
        size="xs"
        variant="transparent"
        square
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
        }}
        sx={isStrikethrough ? ACTIVE_FORMAT_BUTTON_SX : undefined}
      >
        <Strikethrough01Icon size={16} />
      </Button>

      {/* Highlight color dropdown — hidden when editing a template prompt */}
      {!(promptContext?.isTemplate && promptContext.customPrompt) && (
        <SlideEditorHighlightButton />
      )}

      {/* Divider before table controls */}
      <SlideEditorToolbarDivider />

      <TableInsertButton
        editor={editor}
        isInTable={isInTable}
        iconSize={14}
        size="xs"
        variant="transparent"
        square
      />
      {isInTable && (
        <TableActionToolbar size="xs" variant="transparent" square />
      )}
    </>
  );
}

/** Background applied when a format (bold/strikethrough) is active in the
 *  selection. Matches the transparent-variant hover background, so the
 *  toggled-on state is indistinguishable from hover. */
const ACTIVE_FORMAT_BUTTON_SX = {
  '&&': { bgcolor: COLORS.GRAY[100] },
};

export interface SlideEditorFontSizeDropdownProps {
  containerRef: React.Ref<HTMLDivElement>;
  currentFontSize: number | 'mixed' | null;
  blockType: Block;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  /** Override the set of sizes shown in the dropdown. Defaults to ALLOWED_FONT_SIZES (slide editor). */
  allowedFontSizes?: readonly number[];
  /** Override the default font size used for label display and active-size detection. */
  defaultFontSize?: number;
}

export function SlideEditorFontSizeDropdown({
  containerRef,
  currentFontSize,
  blockType,
  isOpen,
  onToggle,
  onClose,
  allowedFontSizes,
  defaultFontSize,
}: SlideEditorFontSizeDropdownProps) {
  const [editor] = useLexicalComposerContext();
  const { theme } = useTextEditorContext();
  // h3's rendered size differs by theme (see headingFontSize); the slide
  // editor renders under presentationContent, diagram text notes under
  // userGeneratedContent.
  const isPresentation = theme === 'presentationContent';
  const sizes = allowedFontSizes ?? ALLOWED_FONT_SIZES;
  const defaultSize = defaultFontSize ?? DEFAULT_FONT_SIZE;

  return (
    <div ref={containerRef}>
      <ButtonWithPopover
        size="xs"
        variant="transparent"
        popperVariant="menuBelow"
        highlightWhenOpen
        isOpen={isOpen}
        onClick={onToggle}
        onClose={onClose}
        popperProps={{
          anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
          transformOrigin: { horizontal: 'left', vertical: 'top' },
          PaperProps: { sx: { mt: '9px' } },
          sx: { zIndex: zIndexes.TOOLTIP },
          disablePortal: false,
        }}
        label={
          <Typography
            variant="body-xs"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            // Inherit the button's text color (primary) instead of the
            // body-xs variant's default secondary color.
            sx={{ color: 'inherit' }}
          >
            {getFontSizeLabel(
              currentFontSize,
              blockType,
              isPresentation,
              defaultSize
            )}
          </Typography>
        }
      >
        {sizes.map((size) => (
          <MenuItem
            key={size}
            label={`${size}pt`}
            onClick={() => {
              // Picking the editor's base size unwraps; any other size wraps.
              editor.dispatchCommand(
                SET_FONT_SIZE_COMMAND,
                size === defaultSize ? null : size
              );
            }}
            sx={{
              fontWeight: isActiveSize(
                size,
                currentFontSize,
                blockType,
                isPresentation,
                defaultSize
              )
                ? 700
                : 400,
            }}
          />
        ))}
      </ButtonWithPopover>
    </div>
  );
}
